"use client";

import { unwrapResult } from "@reduxjs/toolkit";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import useGtmQuestionSet from "@/app/common-hooks/gtm/use-gtm-question-set";
import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import FadeAnimation from "@/components/atoms/animation/fade-animation";
import TransformAnimation from "@/components/atoms/animation/transform-animation";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import UnSavedChangesWarningMessage from "@/components/organisms/un-saved-changes-warning-message/un-saved-changes-warning-message";
import ValidateMedicalsWarningMessage from "@/components/organisms/validate-medicals-warning-message/validate-medicals-warning-message";
import withApplication from "@/components/with-application";
import withBrand from "@/components/with-brand";
import withMedicalFlow from "@/components/with-medical-flow-v2";
import {
  Individual,
  MedicalLandingPage,
  QuestionFour,
  QuestionSeven,
  TravellerEdit,
  OrganizerEdit,
  FinishEdit,
  RenewalPage,
} from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import { PRODUCT_DOMAIN } from "@/constants";
import useReplaceRouterPath from "@/hooks/use-replace-router-path/use-replace-router-path";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import useTurnstile from "@/hooks/use-turnstile/use-turnstile";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { useAppDispatch } from "@/store/hook";
import {
  createQuoteOnQuestion,
  saveQuestionDataBFF,
  sessionCheck,
  updateQuoteOnQuestion,
} from "@/store/slices/quote/quote.action";
import {
  setQuestionData,
  setTurnstileToken,
} from "@/store/slices/quote/quote.slice";
import { RootState } from "@/store/store";
import { Organiser } from "@/types/Organiser";
import {
  FormDataTypeOrganiser,
  OrganiserContainerProps,
} from "@/types/OrganiserType";
import { QuotePayload } from "@/types/quote/quote-data";
import { Traveller } from "@/types/Traveller";
import { TravellerDetails } from "@/types/TravellerDetails";
import { TripDetails } from "@/types/TripDetails";
import selectLoationHookFlash from "@/utils/common.utils";
import { getNationalityPopularCountryList } from "@/utils/filtered-nationality.utils";
import { trimFormValue, trimFormValueOptional } from "@/utils/form-input.utils";
import {
  isFormDataCompleteOrganiser,
  modifyOrganiserForm,
  hidePersonalDetailsFields,
} from "@/utils/organiser.utils";
import {
  jeCodes,
  gyCodes,
  imCodes,
} from "@/utils/question-location-from.utils";
import { smoothScrollToRef } from "@/utils/scrollUtils";
import { setOrganiserPassedFlag } from "@/utils/session.utils";
import {
  formatDate,
  generateTravelerDetails,
  syncOrganiserPMIFromTravellers,
} from "@/utils/traveller.utils";
import { getCurrentToken, resetToken } from "@/utils/turnstile/turnstile.utils";

import OrganiserForm from "./organiser-answer/organiser-form";
import ActionButton from "../../action-button/action-button";
import ConfirmationsModal from "../../quote-section/edit-quote-section/confirmation-modal";
import editQuoteStyle from "../../quote-section/edit-quote-section/edit-quote.module.css";
import BackToRenewal from "../../renewals/back-to-renewal/back-to-renewal";

const initialPayload: QuotePayload = {
  type: "Quote",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
    lastVisitedPage: "test",
  },
};

function OrganiserContainer(props: Readonly<OrganiserContainerProps>) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const editPage = searchParams.get(TravellerEdit);
  const organizerEdit = searchParams.get(OrganizerEdit);
  const { current, wizard } = useWizard();
  const [editModal, setEditModal] = useState(false);
  const dispatch = useAppDispatch();
  const [payload, modifyPayload] = useState<QuotePayload>(initialPayload);
  const [isFormSubmit, setIsFormSubmit] = useState(true);
  const [isSubmitBtnClicked, setSubmitBtnClicked] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const { resetQuestionData } = useResetStateTravelerEdit();
  const { usagePage, ctaRef, formRef, newMedicalFlowEnabled, application } =
    props;

  const mainHeaderRef = useRef<HTMLDivElement | null>(null);

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [organiserFormSaved, setOrganiserFormSaved] = useState<
    FormDataTypeOrganiser | undefined
  >(undefined);

  const [isOrganizerTraveling, setIsOrganizerTraveling] = useState(false);
  const [experimentDetailsPayload, setExperimentDetailsPayload] =
    useState<Record<string, string | null | undefined> | null>(null);
  const pathname = usePathname();
  useReplaceRouterPath(pathname);
  const isAddOrganizerToTravellerList =
    searchParams.get(TravellerEdit) || searchParams.get(OrganizerEdit);

  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState(false);
  const enableNewMedicalFlow =
    process.env.NEXT_PUBLIC_MEDICAL_FLOW_V2_ENABLED === "true";

  const [isValidateMedicalModalOpen, setIsValidateMedicalModalOpen] =
    useState<boolean>(false);

  const quoteResponse = useSelector((state: RootState) => state.quote);
  const isUnsupportedBrowser = !!quoteResponse?.isUnsupportedBrowser;

  const { isTurnstileEnabled } = useTurnstile();

  // Generate fresh turnstile token on mount
  useEffect(() => {
    if (isTurnstileEnabled) {
      resetToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (quoteResponse?.attributes?.organiser) {
      const modifiedOrganiserData: FormDataTypeOrganiser = modifyOrganiserForm(
        quoteResponse?.attributes?.organiser,
        quoteResponse?.attributes?.tripDetails?.startDate,
        current?.uiElementControls?.getAgeFromPolicyStartForAMT,
        quoteResponse?.attributes?.tripDetails?.policyType
      );
      if (newMedicalFlowEnabled) {
        if (
          quoteResponse?.attributes?.travellerDetails?.medicalTermsAndConditions
        ) {
          modifiedOrganiserData.hasMedicalConditions = false;
        }
      }
      setOrganiserFormSaved(modifiedOrganiserData);
    }
  }, [
    quoteResponse?.attributes?.organiser,
    quoteResponse?.attributes?.travellerDetails?.medicalTermsAndConditions,
    newMedicalFlowEnabled,
    quoteResponse?.attributes?.tripDetails?.startDate,
    quoteResponse?.attributes?.tripDetails?.policyType,
    current?.uiElementControls?.getAgeFromPolicyStartForAMT,
  ]);

  useEffect(() => {
    if (quoteResponse?.attributes?.tripDetails) {
      const { tripDetails, organiser } = quoteResponse.attributes;
      let payloadModify: QuotePayload;
      if (quoteResponse?.referenceId) {
        let attributes = {
          ...quoteResponse?.attributes,
          tripDetails,
          organiser,
        };

        if (props?.travellerType === Individual && organiser?.travelling) {
          const travellerDetails = {
            traveller: generateTravelerDetails(
              [],
              organiser,
              !!isAddOrganizerToTravellerList
            ),
          };
          attributes = {
            ...attributes,
            ...{ travellerDetails },
          };
        }

        payloadModify = {
          ...quoteResponse,
          attributes: {
            ...attributes,
          },
        };
      } else {
        payloadModify = {
          ...initialPayload,
          attributes: {
            ...initialPayload.attributes,
            partnerCode: quoteResponse?.attributes?.partnerCode,
            tripDetails,
            organiser,
          },
        };
      }
      modifyPayload(payloadModify);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quoteResponse?.attributes.organiser,
    quoteResponse?.attributes.tripDetails,
  ]);

  useEffect(() => {
    // Set flag when user reaches organizer page
    setOrganiserPassedFlag();
  }, []);

  const handleTravelingFromLocation = (e?: string): string => {
    const getLocation = (code?: string) => {
      const normalizedCode = code?.toUpperCase();

      switch (true) {
        case jeCodes.some((jeCode) => normalizedCode?.startsWith(jeCode)):
          return "UK5";
        case gyCodes.some((gyCode) => normalizedCode?.startsWith(gyCode)):
          return "UK4";
        case imCodes.some((imCode) => normalizedCode?.startsWith(imCode)):
          return "UK3";
        default:
          return (
            (current?.brandSpecificParams?.fromCountryCode as string) ?? "UK1"
          );
      }
    };

    return getLocation(e);
  };

  const accessFormData = (formData: FormDataTypeOrganiser) => {
    // Get experimentDetails from Redux (for email flow validation)
    const experimentDetailsFromBackend =
      quoteResponse.attributes.experimentDetails;

    if (
      isFormDataCompleteOrganiser(
        formData,
        props?.travellerType,
        wizard,
        current,
        experimentDetailsFromBackend
      )
    ) {
      setIsFormSubmit(false);

      // Validate variant status using both backend response and Optimizely
      const isPersonalDetailsDisabled = hidePersonalDetailsFields(
        experimentDetailsFromBackend
      );

      // Build experimentDetails ONLY for Spain variant users (not control, not other brands)
      const experimentDetails = {
        ...experimentDetailsFromBackend,
        deferOrganiserDetailsFlow: hidePersonalDetailsFields(
          experimentDetailsFromBackend
        )
          ? "variant"
          : "control",
      };
      setExperimentDetailsPayload(experimentDetails);

      const fromCountryLocation = handleTravelingFromLocation(
        formData.postCode
      );

      let organiser: Organiser = {
        title: formData.value1,
        firstName: trimFormValue(formData.value2),
        lastName: trimFormValue(formData.value3),
        nationalityId: formData?.nationalityValue,
        dateOfBirth: formatDate(formData?.dateValue),
        travelling: formData.travelling,
        email: trimFormValueOptional(formData?.email),
        telephone: trimFormValueOptional(formData?.telephone),
        // Preserve existing PMI status if it exists
        hasPMI: quoteResponse?.attributes?.organiser?.hasPMI,
        country: formData.country,
        // Address fields: use form data if not in Spain variant
        city: isPersonalDetailsDisabled
          ? undefined
          : trimFormValueOptional(formData.city),
        postCode: isPersonalDetailsDisabled
          ? undefined
          : trimFormValueOptional(formData.postCode),
        firstLineOfAddress: isPersonalDetailsDisabled
          ? undefined
          : trimFormValueOptional(formData.firstLineOfAddress),
        secondLineOfAddress: isPersonalDetailsDisabled
          ? undefined
          : trimFormValueOptional(formData.secondLineOfAddress),
        // Only include identification fields for Spain product and not in variant
        ...(current?.brandSpecificParams?.showIdentificationFields &&
          !isPersonalDetailsDisabled && {
            identificationType: formData.identificationType,
            identificationNumber: trimFormValueOptional(
              formData.identificationNumber
            ),
          }),
      };

      // In edit flows, sync organiser PMI from travellers array only if traveling status hasn't changed
      if (
        (editPage || organizerEdit) &&
        quoteResponse?.attributes?.travellerDetails?.traveller &&
        quoteResponse?.attributes?.organiser?.travelling === formData.travelling
      ) {
        organiser = syncOrganiserPMIFromTravellers(
          organiser,
          quoteResponse.attributes.travellerDetails.traveller
        );
      }

      if (current.brandSpecificParams?.postCodeOptional && !formData.postCode) {
        if (quoteResponse.attributes?.organiser?.postCode) {
          organiser.postCode = "";
        } else {
          delete organiser.postCode;
        }
      }

      setIsOrganizerTraveling(!!formData.travelling);
      const { travellerType } = props;
      const { tripDetails } = quoteResponse.attributes;
      const { attributes: payloadAttributes } = payload;
      let attributes = {
        tripDetails: {
          ...tripDetails,
          ...(current.uiElementControls?.locationFrom === false && {
            fromLocation: fromCountryLocation,
          }),
        },
        organiser,
        marketingChannelDetails:
          quoteResponse?.attributes?.marketingChannelDetails,
      };
      const organiserTravellerId = sessionStorage.getItem(
        "organiserTravellerId"
      );

      if (travellerType === Individual && organiser.travelling) {
        if (quoteResponse?.referenceId) {
          const traveller =
            quoteResponse?.attributes?.travellerDetails?.traveller;
          if (traveller && traveller.length > 0) {
            const travellingOrganiser = traveller.find(
              (obj: Traveller) => obj?.sequence === 1
            );
            sessionStorage.setItem(
              "organiserTravellerId",
              travellingOrganiser?.encryptedId ?? ""
            );
          }
        }
        const travellerDetails: TravellerDetails = {
          traveller: generateTravelerDetails(
            [],
            organiser,
            !!isAddOrganizerToTravellerList
          ),
        };
        if (newMedicalFlowEnabled) {
          travellerDetails.medicalTermsAndConditions =
            formData.hasMedicalConditions === undefined
              ? undefined
              : formData.hasMedicalConditions === false;
        }

        attributes = {
          ...attributes,
          ...{ travellerDetails },
        };
      } else if (travellerType === Individual && !organiser.travelling) {
        const travellerDetails: TravellerDetails = {
          traveller:
            quoteResponse?.attributes?.travellerDetails?.traveller ?? [],
        };
        if (newMedicalFlowEnabled) {
          travellerDetails.medicalTermsAndConditions = quoteResponse?.attributes
            ?.organiser?.travelling
            ? undefined
            : quoteResponse?.attributes?.travellerDetails
                ?.medicalTermsAndConditions;
        }

        attributes = {
          ...attributes,
          ...{ travellerDetails },
        };
      } else if (quoteResponse?.referenceId) {
        attributes = {
          ...quoteResponse.attributes,
          ...attributes,
        };
      }

      if (
        quoteResponse?.attributes.organiser?.travelling &&
        organiser.travelling === false &&
        !organiserTravellerId
      ) {
        if (quoteResponse?.attributes?.travellerDetails) {
          const traveller =
            quoteResponse?.attributes?.travellerDetails?.traveller;
          if (traveller && traveller.length > 0) {
            const travellingOrganiser = traveller.find(
              (obj: Traveller) => obj?.sequence === 1
            );
            if (travellingOrganiser) {
              sessionStorage.setItem(
                "organiserTravellerId",
                travellingOrganiser?.encryptedId || ""
              );
            }
          }
        }
      }

      if (
        !quoteResponse?.attributes.organiser?.travelling &&
        organiser.travelling &&
        !organiserTravellerId
      ) {
        if (quoteResponse?.attributes?.travellerDetails) {
          const traveller =
            quoteResponse?.attributes?.travellerDetails?.traveller;
          if (traveller && traveller.length > 0) {
            sessionStorage.setItem("organiserTravellerId", "notapply");
          }
        }
      }

      const modifiedAttributes = {
        ...payloadAttributes,
        ...attributes,
      };
      modifyPayload({
        ...(quoteResponse?.referenceId ? quoteResponse : payload),
        attributes: modifiedAttributes,
      });
    } else {
      setIsFormSubmit(true);
    }
  };

  const handleSubmitButton = async () => {
    if (!isFormSubmit) {
      setSubmitBtnClicked(true);
      setButtonLoading(true);

      try {
        sessionStorage.setItem(
          "isPrevMedicalAvailable",
          quoteResponse.attributes?.travellerDetails?.traveller
            ?.some((traveller: Traveller) => traveller.medical !== undefined)
            ?.toString() ?? "false"
        );
        const isIndividualTraveller = props?.travellerType === Individual;
        const hasOrganiserTravelling =
          payload?.attributes?.organiser?.travelling;
        const hasReferenceId = quoteResponse?.referenceId;

        if (isIndividualTraveller && hasOrganiserTravelling) {
          if (
            (editPage || organizerEdit) &&
            hasReferenceId &&
            !enableNewMedicalFlow
          ) {
            dispatch(setQuestionData(payload as QuotePayload));
            if (editPage) {
              router.push(current.edges?.toTravHealthTrvEditNext ?? "");
            } else {
              router.push(`/${MedicalLandingPage}/?${OrganizerEdit}=true`);
            }
          } else if (
            (editPage || organizerEdit) &&
            hasReferenceId &&
            enableNewMedicalFlow
          ) {
            dispatch(setQuestionData(payload as QuotePayload));
            if (editPage) {
              if (newMedicalFlowEnabled) {
                router.push(current.edges.toMedicalConfTrvEditNext ?? "");
              } else {
                router.push(current.edges.toTravHealthTrvEditNext ?? "");
              }
            } else if (newMedicalFlowEnabled) {
              router.push(current.edges.toMedicalConfOrgEditNext ?? "");
            } else {
              router.push(current.edges.toTravHealthOrgEditPrev ?? "");
            }
          } else {
            const result = await dispatch(
              !editPage && !organizerEdit && hasReferenceId
                ? updateQuoteOnQuestion({
                    payload: {
                      ...payload,
                      viewPage: Routes.Organiser,
                      // Add experimentDetails for updateQuoteOnQuestion API
                      ...(current?.brandSpecificParams?.enableExperimentData &&
                        experimentDetailsPayload && {
                          attributes: {
                            ...payload.attributes,
                            experimentDetails: experimentDetailsPayload,
                          },
                        }),
                    },
                    router,
                    dispatch,
                  })
                : createQuoteOnQuestion({
                    payload: {
                      ...payload,
                      viewPage: Routes.Organiser,
                      ...(isTurnstileEnabled && {
                        turnstileErrorCode: null,
                        isUnsupportedBrowser: false,
                      }),
                      // Add experimentDetails for createQuoteOnQuestion API
                      ...(current?.brandSpecificParams?.enableExperimentData &&
                        experimentDetailsPayload && {
                          attributes: {
                            ...payload.attributes,
                            experimentDetails: experimentDetailsPayload,
                          },
                        }),
                    },
                    router,
                    dispatch,
                  })
            );
            const originalPromiseResult = unwrapResult(result);
            if (originalPromiseResult) {
              if (newMedicalFlowEnabled) {
                router.push(current.edges.medicalV2EnabledNext ?? "");
              } else {
                router.push(current.edges.medicalV2DisabledNext ?? "");
              }
              sessionStorage.removeItem("initialLoad");
              sessionStorage.removeItem("organiserTravellerId");
            }
          }
        } else if (organizerEdit && hasReferenceId && !hasOrganiserTravelling) {
          const result = await dispatch(
            updateQuoteOnQuestion({
              payload: {
                ...payload,
                viewPage: Routes.Organiser,
                // Add experimentDetails for updateQuoteOnQuestion API
                ...(current?.brandSpecificParams?.enableExperimentData &&
                  experimentDetailsPayload && {
                    attributes: {
                      ...payload.attributes,
                      experimentDetails: experimentDetailsPayload,
                    },
                  }),
              },
              dispatch,
              router,
            })
          );
          const originalPromiseResult = unwrapResult(result);
          if (originalPromiseResult) {
            router.push(current.edges.toQoutePageNext ?? "");
            return;
          }
        } else if (!hasReferenceId) {
          dispatch(setQuestionData(payload as QuotePayload));
          const questionData = await dispatch(
            saveQuestionDataBFF({
              payload: { ...payload, viewPage: Routes.Organiser },
              dispatch,
              router,
            })
          );
          const originalPromiseResult = unwrapResult(questionData);
          if (originalPromiseResult) {
            router.push(current.edges.toTravellersNext ?? "");
            return;
          }
        } else {
          if (hasReferenceId && !(editPage || organizerEdit)) {
            dispatch(setQuestionData(payload as QuotePayload));
            const questionData = await dispatch(
              saveQuestionDataBFF({
                payload: { ...payload, viewPage: Routes.Organiser },
                dispatch,
                router,
              })
            );
            const originalPromiseResult = unwrapResult(questionData);
            if (originalPromiseResult) {
              router.push(current.edges.toTravellersNext ?? "");
              return;
            }
          } else {
            dispatch(setQuestionData(payload as QuotePayload));
          }
          dispatch(sessionCheck({ dispatch, router }));
          let path: string | undefined = current.next ?? "";
          if (editPage || organizerEdit) {
            path = editPage
              ? current.edges.toTravellersTrvEditNext
              : current.edges.toTravellersOrgEditNext;
          }
          router.push(path ?? "");
        }
        setButtonLoading(false);
        if (isTurnstileEnabled && hasOrganiserTravelling) {
          const currentTurnstileToken = getCurrentToken();
          dispatch(setTurnstileToken(currentTurnstileToken));
        }
      } catch {
        setButtonLoading(false);
      }
    } else {
      setShowErrors(true);
    }
  };

  const toLocationData = selectLoationHookFlash(
    payload?.attributes?.tripDetails as TripDetails
  );

  useGtmQuestionSet({
    pageTitle: QuestionSeven,
    tripType: payload?.attributes?.tripDetails?.policyType,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    tripExtra: payload?.attributes?.tripDetails?.goingOnCruise
      ? QuestionFour
      : undefined,
    coverStartDate: payload?.attributes?.tripDetails?.startDate || undefined,
    coverEndDate: payload?.attributes?.tripDetails?.endDate || undefined,
    cruise: payload?.attributes?.tripDetails?.goingOnCruise ?? undefined,
    travelFrom: payload?.attributes?.tripDetails?.fromLocation || undefined,
    travelGroupType: payload?.attributes?.tripDetails?.partyType || undefined,
    isSubmitBtnClicked,
  });

  const handleBackButton = () => {
    let path = current.prev ?? "";
    if (editPage) {
      // Check if user came from quote-review (sessionStorage is set)
      const editSection = getQuoteReviewSummaryEditSection();
      if (editSection === "organiser") {
        // User came from quote-review, navigate to quote-review
        path = current.edges.travellerEditQuoteSummaryPrev ?? "";
      } else {
        // User came from quote page, use old flow
        path = current.edges.editFlowPrev ?? "";
      }
    }
    router.push(path);
  };

  const handleCancelEdit = () => {
    setEditModal(true);
  };

  const handleUnSavedWarningMessageContinueBtn = () => {
    setIsOpenUnsavedChangesModal(false);
    resetQuestionData();
    router.push("/");
  };

  const nationalityPopularCountryListData =
    props?.pageContent?.nationalityPopularCountryList;

  const isTurnstileTokenAvailable = useMemo(
    () => isTurnstileEnabled && !!quoteResponse?.turnstileToken,
    [isTurnstileEnabled, quoteResponse?.turnstileToken]
  );

  const renderQuestionOneSection = () => {
    return (
      <div data-testid="organiser-support-content">
        {" "}
        {(editPage || organizerEdit) && application !== RenewalPage && (
          <div>
            <LabelButtonWrapper
              hierarchy="tertiary"
              label={
                props?.pageContent?.cancelEditingBtnText || "Cancel editing"
              }
              id="cancel-edit-organiser"
              className={`${editQuoteStyle.cancelEditButton}`}
              onClick={handleCancelEdit}
            />
            <ConfirmationsModal
              isOpen={editModal}
              handleConfirmBtnClick={() => {
                resetQuestionData();
                // Check if edit originated from quote-review page
                const editSection = getQuoteReviewSummaryEditSection();
                if (editSection) {
                  // Navigate back to quote-review page
                  router.push(current.edges.cancelQuoteReviewSummaryEdit ?? "");
                } else {
                  // Use default cancelEdit edge (navigate to quote page)
                  router.push(current.edges.cancelEdit ?? "");
                }
              }}
              handleCancelmBtnClick={() => {
                setEditModal(false);
              }}
            />
          </div>
        )}
        <h1
          className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
          md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center md:px-0 px-spacing-m"
          ref={mainHeaderRef}
        >
          {props?.pageContent?.questionText && props?.pageContent?.questionText}
        </h1>
        <OrganiserForm
          firstInputClick={() => smoothScrollToRef(mainHeaderRef, 100)}
          inputOnePlaceholder={props?.pageContent?.inputOnePlaceholder}
          inputTwoPlaceholder={props?.pageContent?.inputTwoPlaceholder}
          handleSubmitButton={accessFormData}
          inputOneErrorNumericvalues={
            props?.pageContent?.inputOneErrorNumericvalues
          }
          inputOneErrorSpecialCharacters={
            props?.pageContent?.inputOneErrorSpecialCharacters
          }
          inputOneErrorMaximumNumber={
            props?.pageContent?.inputOneErrorMaximumNumber
          }
          inputTwoErrorNumericvalues={
            props?.pageContent?.inputTwoErrorNumericvalues
          }
          inputTwoErrorSpecialCharacters={
            props?.pageContent?.inputTwoErrorSpecialCharacters
          }
          inputTwoErrorMaximumNumber={
            props?.pageContent?.inputTwoErrorMaximumNumber
          }
          dobMinAge={props?.pageContent?.dobMinAge}
          dobMaxAge={props?.pageContent?.dobMaxAge}
          dobPlaceholder={props?.pageContent?.dobPlaceholder}
          travellerType={props?.travellerType}
          personalStatusPlaceholder={
            props?.pageContent?.personalStatusPlaceholder
          }
          nationalityPlaceholder={props?.pageContent?.nationalityPlaceholder}
          nationalityPopularCountryList={getNationalityPopularCountryList(
            nationalityPopularCountryListData
          )}
          ageFamilyFullTimeEducation={
            props?.pageContent?.ageFamilyFullTimeEducation
          }
          ageSingleParentFullTimeEducation={
            props?.pageContent?.ageSingleParentFullTimeEducation
          }
          addressLineOne={props?.pageContent?.addressLineOne}
          addressLineTwo={props?.pageContent?.addressLineTwo}
          TownCityPlaceholder={props?.pageContent?.TownCityPlaceholder}
          countryPlaceholder={props?.pageContent?.countryPlaceholder}
          postcodePlaceholder={props?.pageContent?.postcodePlaceholder}
          addressBlockButton={props?.addressBlockButton}
          addressLineOneErrorMasg={props?.pageContent?.addressLineOneErrorMasg}
          addressLineTwoErrorMasg={props?.pageContent?.addressLineTwoErrorMasg}
          adressInputPlaceholder={props?.pageContent?.adressInputPlaceholder}
          TownCityErrorMasg={props?.pageContent?.TownCityErrorMasg}
          postcodeErrorMasg={props?.pageContent?.postcodeErrorMasg}
          adressInputErrorMasg={props?.pageContent?.adressInputErrorMasg}
          questionTwoText={props?.pageContent?.questionTwoText || ""}
          enterAddressButton={props?.pageContent?.enterAddressButton}
          searchByPostCodeButton={props?.pageContent?.searchByPostCodeButton}
          organiserFormSaved={organiserFormSaved}
          dobMaxLimit={props?.pageContent?.dobMaxLimit}
          usagePage={usagePage}
          newMedicalFlowEnabled={newMedicalFlowEnabled}
          medicalQuestionText={props?.pageContent?.medicalQuestionText ?? ""}
          medicalQuestionInfoText={
            props?.pageContent?.medicalQuestionInfoText ?? ""
          }
          medicalServiceDeniedText={
            props?.pageContent?.medicalServiceDeniedText ?? ""
          }
          medicalYesBtnText={props?.pageContent?.medicalYesBtnText ?? ""}
          medicalNoBtnText={props?.pageContent?.medicalNoBtnText ?? ""}
          renewalInfoText={props?.pageContent?.renewalNonEditInfoBox}
          alertMessageIncompleteSectionSingle={
            props?.pageContent?.alertMessageIncompleteSectionSingle
          }
          alertMessageIncompleteSection={
            props?.pageContent?.alertMessageIncompleteSection
          }
          alertMessageLinkSectionSingle={
            props?.pageContent?.alertMessageLinkSectionSingle
          }
          alertMessageLinkSection={props?.pageContent?.alertMessageLinkSection}
          showErrors={showErrors}
          identificationTypePlaceholder={
            props?.pageContent?.identificationTypePlaceholder
          }
          identificationNumberPlaceholder={
            props?.pageContent?.identificationNumberPlaceholder
          }
          identificationTypeRequiredError={
            props?.pageContent?.identificationTypeRequiredError
          }
          identificationNumberInvalidError={
            props?.pageContent?.identificationNumberInvalidError
          }
          identificationNumberWarning={
            props?.pageContent?.identificationNumberWarning
          }
          identificationTypeDNILabel={
            props?.pageContent?.identificationTypeDNILabel
          }
          identificationTypeNIELabel={
            props?.pageContent?.identificationTypeNIELabel
          }
          identificationTypePassportLabel={
            props?.pageContent?.identificationTypePassportLabel
          }
        />
      </div>
    );
  };

  const hasOrganiserTravellingChanged = () => {
    return (
      quoteResponse?.attributes?.organiser?.travelling !==
        payload?.attributes?.organiser?.travelling && !isFormSubmit
    );
  };

  const { hasMedicalCondition } = useQuestionsState();

  return (
    <div
      data-testid="question-organiser-test-id"
      className={`rounded-radius-xxl 
        bg-surface-neutral-option-1 rounded-b-radius-xxl  
        md:pt-spacing-xxl md:pb-spacing-xxl pt-spacing-m pb-0 px-spacing-m h-full`}
    >
      <div className="lg:w-[414px] md:w-[400px] w-full mx-auto relative h-full flex flex-col justify-between">
        {props?.application === "renewal" &&
          props?.pageContent?.backToRenewalsText &&
          editPage && (
            <div>
              <BackToRenewal
                label={props?.pageContent?.backToRenewalsText}
                className="pb-[0px] 
        top-[-2px] !px-[0px] mb-spacing-l md:!mb-spacing-l lg:mx-[0px]"
                onClick={() => {
                  setIsOpenUnsavedChangesModal(true);
                }}
              />
            </div>
          )}
        <UnSavedChangesWarningMessage
          isOpen={isOpenUnsavedChangesModal}
          onContinueClick={handleUnSavedWarningMessageContinueBtn}
          setIsOpen={setIsOpenUnsavedChangesModal}
        />
        <ValidateMedicalsWarningMessage
          isOpen={isValidateMedicalModalOpen}
          onContinueClick={() => {
            setIsValidateMedicalModalOpen(false);
            handleSubmitButton();
          }}
          setIsOpen={setIsValidateMedicalModalOpen}
        />
        <TransformAnimation animationRef={formRef} currentPage={usagePage}>
          {renderQuestionOneSection()}
        </TransformAnimation>
        <FadeAnimation animationRef={ctaRef}>
          <div id="organiser-main-cta-button-id">
            <ActionButton
              page={props?.usagePage}
              ctaButtonText={
                organizerEdit && !isOrganizerTraveling
                  ? props?.pageContent?.finishEditingBtnText || FinishEdit
                  : props?.pageContent?.ctaButtonText || ""
              }
              disableMain={
                hasMedicalCondition ||
                (!editPage &&
                  !organizerEdit &&
                  isTurnstileEnabled &&
                  !isTurnstileTokenAvailable)
              }
              isLoading={
                buttonLoading ||
                (!editPage &&
                  !organizerEdit &&
                  isTurnstileEnabled &&
                  !isTurnstileTokenAvailable &&
                  !isUnsupportedBrowser)
              }
              submitButtonClick={() => {
                if (
                  application === RenewalPage &&
                  hasOrganiserTravellingChanged()
                ) {
                  setIsValidateMedicalModalOpen(true);
                } else {
                  handleSubmitButton();
                }
              }}
              backButtonClick={handleBackButton}
              showBackBtn={!organizerEdit}
            />
          </div>
        </FadeAnimation>
      </div>
    </div>
  );
}
export default withBrand(withMedicalFlow(withApplication(OrganiserContainer)));
